import { useFormikContext } from 'formik';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useCallback } from 'react';
import * as yup from 'yup';

import { api, isDefined, isTruthy } from 'common';

import { SHORT_CODE_REGEX } from './string';

export function countErrors(errors: unknown) {
  if (!errors) {
    return 0;
  }

  if (typeof errors !== 'object') {
    return 1;
  }

  let numErrors = 0;

  for (const value of Object.values(errors)) {
    if (!value) {
      continue;
    }

    if (typeof value === 'string') {
      numErrors++;
    } else if (Array.isArray(value)) {
      for (const item of value) {
        numErrors += countErrors(item);
      }
    } else if (typeof value === 'object') {
      numErrors += countErrors(value);
    } else if (value) {
      numErrors++;
    }
  }
  return numErrors;
}

export function yupEnum(obj: object, additionalAllowedValues: any[] = []) {
  const possibilities = [...Object.values(obj), ...additionalAllowedValues];
  return yup.mixed().oneOf(possibilities);
}

interface YupUsPhoneNumberOptions {
  allowShortCode?: boolean;
}

export function yupUsPhoneNumber(options: YupUsPhoneNumberOptions = {}) {
  const { allowShortCode = false } = options;
  const message = allowShortCode
    ? 'Enter a valid US phone number or short code (5-6 digits)'
    : 'Enter a valid US phone number';
  return yup.string().test('test-phone-number', message, (value) => {
    if (!value) {
      return true;
    }
    if (allowShortCode && SHORT_CODE_REGEX.test(value)) {
      return true;
    }
    return isValidPhoneNumber(value, 'US');
  });
}

export function yupSipUri() {
  return yup
    .string()
    .matches(
      /^sip:[A-Za-z0-9._+-]+@[a-z0-9.-]+$/,
      'Enter a canonical SIP URI in the form sip:user@host (lowercase host; user-part limited to letters, digits, ., _, +, -; no port, no transport parameters, no query)'
    )
    .test(
      'sip-user-part-e164',
      'Numeric SIP user-parts must be a valid phone number in E.164 form (e.g. sip:+12025550100@host); the inbound webhook normalizes to E.164 before trigger lookup, so a non-E.164 or invalid number will never match',
      (value) => {
        if (!isTruthy(value)) {
          return true;
        }
        const userPartMatch = /^sip:([^@]+)@/.exec(value);
        if (!userPartMatch) {
          return true;
        }
        const userPart = userPartMatch[1];
        if (!/^\+?\d+$/.test(userPart)) {
          return true;
        }
        return isValidPhoneNumber(userPart);
      }
    );
}

export function yupZipCode() {
  return yup.string().matches(/^\d+$/, 'The field should have digits only').length(5);
}

export function yupS3SafePath() {
  return yup
    .string()
    .matches(
      /^(?!\/)[\w-/]*$/,
      'File path can only contain letters, numbers, hyphens, and underscores, and cannot start with a slash'
    );
}

export function yupAlphanumericWithSpaces() {
  return yup.string().matches(/^[a-zA-Z0-9\s]+$/, 'Only letters, numbers, and spaces are allowed');
}

export function yupLatitude() {
  return yup
    .number()
    .min(-90, 'Latitude must be between -90 and 90')
    .max(90, 'Latitude must be between -90 and 90');
}
export function yupLongitude() {
  return yup
    .number()
    .min(-180, 'Longitude must be between -180 and 180')
    .max(180, 'Longitude must be between -180 and 180');
}

export const nullUndefinedOrEmpty = [null, undefined, ''];

// Version-independent UUID check to replace yup.string().uuid(), which is not
// compatible with UUIDv7.
export function yupUuid(message?: string) {
  return yup
    .string()
    .matches(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i, message);
}

export interface ParseFormNumberInputProps {
  value: string | number | null | undefined;
}

/**
 * Parses form number input to a number or null.
 * Handles edge cases from HTML number inputs:
 * - Empty strings from cleared inputs
 * - String numbers from user input (both integers and decimals)
 * - Null/undefined values
 */
export function parseFormNumberInput(props: ParseFormNumberInputProps): number | null {
  const { value } = props;

  if (!isDefined(value) || value === '') {
    return null;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  if (typeof value === 'number') {
    return value;
  }

  return null;
}

type ErrorCollection = [string, string | string[]][];

/**
 * Pulls error messages out of a ZodError.format() serialized object
 */
export function collectZodErrors(err: any): ErrorCollection {
  const collector: ErrorCollection = [];
  collectZodErrorsInner(err, collector, []);
  return collector;
}

function collectZodErrorsInner(err: unknown, collector: ErrorCollection, path: string[]) {
  if (typeof err !== 'object' || !err) {
    return;
  }

  if (Array.isArray(err)) {
    throw new Error(`Not implemented: ${path.join('.')}`);
  }

  if ('_errors' in err && Array.isArray(err._errors) && err._errors.length) {
    collector.push([path.join('.'), err._errors]);
    return;
  }

  for (const [key, value] of Object.entries(err)) {
    if (key === '_errors') {
      continue;
    }
    path.push(key);
    collectZodErrorsInner(value, collector, path);
    path.pop();
  }
}

const zodFormattedErrorSchema = yup.object({
  type: yup.mixed().oneOf(['input_error']),
  errors: yup.object<any>(),
  source: yup.string().oneOf(['body']),
});

/**
 * Pulls error information out of a vivaa-api exception and transforms
 * it into a format that can be used by Formik's setFieldError.
 * It looks for errors within a zod error format.
 */
export function tryAdaptZodError(possibleZodError: unknown): ErrorCollection | false {
  if (!(possibleZodError instanceof api.Common.APIError)) {
    return false;
  }

  if (
    possibleZodError.status !== 400 ||
    typeof possibleZodError.payload !== 'object' ||
    !possibleZodError.payload
  ) {
    return false;
  }

  const { payload } = possibleZodError;
  if (zodFormattedErrorSchema.isValidSync(payload)) {
    return collectZodErrors(payload.errors);
  }

  return false;
}

export function getErrorMessages(error: unknown):
  | {
      errorsByFieldName: Record<string, string>;
      errors: string[];
    }
  | undefined {
  const adaptedError = tryAdaptZodError(error);
  if (!adaptedError) {
    return undefined;
  }
  const errorsByFieldName: Record<string, string> = {};
  const errors: string[] = [];
  for (const [fieldName, error] of adaptedError) {
    const fieldErrorMessage = Array.isArray(error) ? error.join(', ') : error;
    errorsByFieldName[fieldName] = fieldErrorMessage;
    errors.push(`${fieldName}: ${fieldErrorMessage}`);
  }
  return {
    errorsByFieldName,
    errors,
  };
}

/**
 * Attempt to adapt Zod errors into Formik field errors by field name.
 */
interface UpdateFormikFieldErrorsProps {
  possibleZodError: unknown;
  setFieldError: (fieldName: string, message: string) => void;
  fieldPrefix?: string;
}
export function updateFormikFieldErrors(args: UpdateFormikFieldErrorsProps): boolean {
  const { possibleZodError, setFieldError, fieldPrefix } = args;
  const adaptedErrors = tryAdaptZodError(possibleZodError);
  if (adaptedErrors) {
    for (const [fieldSuffix, adaptedError] of adaptedErrors) {
      const fieldName = isTruthy(fieldPrefix) ? `${fieldPrefix}.${fieldSuffix}` : fieldSuffix;
      setFieldError(
        fieldName,
        Array.isArray(adaptedError) ? adaptedError.join(', ') : adaptedError
      );
    }
    return true;
  }
  return false;
}

interface useAdaptErrorProps {
  fieldPrefix?: string;
  possibleZodError: unknown;
}
/**
 * Attempt to adapt Zod errors into Formik field errors by field name.
 * Must be used within a Formik context.
 */
export function useUpdateFormikFieldErrors() {
  const { setFieldTouched, setFieldError } = useFormikContext();
  const updateFormikFieldErrors = useCallback(
    async (props: useAdaptErrorProps) => {
      const { fieldPrefix, possibleZodError } = props;
      const adaptedError = tryAdaptZodError(possibleZodError);
      if (adaptedError) {
        for (const [fieldSuffix, error] of adaptedError) {
          const fieldName = isTruthy(fieldPrefix) ? `${fieldPrefix}.${fieldSuffix}` : fieldSuffix;
          await setFieldTouched(fieldName, true, false);
          setFieldError(fieldName, Array.isArray(error) ? error.join(', ') : error);
        }
      }
    },
    [setFieldTouched, setFieldError]
  );
  return { updateFormikFieldErrors };
}

export function getCharacterLimitText(args: { value?: string; characterLimit: number }) {
  const { value, characterLimit } = args;

  return `${value?.length || 0} of ${characterLimit} max characters`;
}
